import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { Calendar, LogOut, RefreshCw, Plug, CheckCircle, Clock, MapPin, Users, ExternalLink, Download, ShieldCheck, Loader2, ChevronLeft, ChevronRight, Trash2, Plus, CheckSquare, Square, X, Eye, EyeOff, Phone, MessageCircle, AlignLeft, UserPlus, BookUser, Search, Pencil, Zap, Circle, Check, GripVertical, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/nasrallah`;
const TOKEN_KEY = 'nasrallah_token';

// ─── Auth helpers ───────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem(TOKEN_KEY) || '';
const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearToken = () => localStorage.removeItem(TOKEN_KEY);
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ─── Login screen ──────────────────────────────────────────────────────────
const LoginScreen = ({ onLoggedIn }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      // Trim spaces and lowercase the username so users can't get stuck
      // by an accidental capital letter or trailing space (common on mobile).
      const r = await axios.post(`${API}/auth/login`, {
        username: (username || '').trim().toLowerCase(),
        password: (password || '').trim(),
      });
      setToken(r.data.token);
      onLoggedIn(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'שגיאה בהתחברות');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4" dir="rtl" data-testid="nasrallah-login">
      <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(ellipse at top, #dbeafe 0%, transparent 60%)' }} />
      <form onSubmit={submit} className="relative z-10 w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
        <div className="flex flex-col items-center mb-6">
          <img src="/nasrallah-logo.png" alt="לוגו" className="w-56 h-auto mb-4" data-testid="login-logo" />
          <p className="text-center text-slate-500 text-xs">משרד ראיית חשבון · סביבת עבודה פרטית</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-slate-700 text-xs font-bold mb-1 block">שם משתמש</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
              required data-testid="login-username"
              placeholder="הקלד שם משתמש"
              autoComplete="username"
              autoCapitalize="off"
              spellCheck="false"
              dir="ltr"
              className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 text-left" />
          </div>
          <div>
            <label className="text-slate-700 text-xs font-bold mb-1 block">סיסמה</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} required data-testid="login-password"
                placeholder="הקלד סיסמה"
                dir="ltr"
                autoComplete="current-password"
                className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-lg px-3 py-2.5 pl-10 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 text-left" />
              {/* Eye toggle — left side because direction is RTL but the
                  input is LTR, so visually the icon ends up next to where
                  the user types (right side of the input). */}
              <button type="button" onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                data-testid="login-toggle-password"
                aria-label="הצג/הסתר סיסמה">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-600 text-xs font-medium" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full bg-gradient-to-l from-blue-700 to-blue-800 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 text-white font-black rounded-lg py-3 transition-all shadow-md">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'כניסה'}
          </button>
        </div>
        <p className="mt-6 text-center text-[10px] text-slate-400">
          סביבת עבודה פרטית · אין גישה ללא סיסמה
        </p>
      </form>
    </div>
  );
};

// ─── Layout shell (after login) ─────────────────────────────────────────────
const Shell = ({ user, onLogout, headerExtra, children }) => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" dir="rtl" data-testid="nasrallah-shell">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/nasrallah-logo.png" alt="לוגו" className="h-9 w-auto" />
          <div>
            <p className="text-sm font-bold tracking-tight text-slate-900">נסראללה אבתיסאם</p>
            <p className="text-[10px] text-slate-500">משרד ראיית חשבון</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {headerExtra}
          <span className="text-xs text-slate-600 hidden sm:inline">{user?.name}</span>
          <button onClick={onLogout} data-testid="logout-btn"
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg p-2 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      {children}
    </div>
  );
};

// ─── Calendar dashboard ─────────────────────────────────────────────────────
const HEBREW_MONTHS = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];

// Detect device family for the install instructions modal.
const detectDevice = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};

const CalendarPage = ({ user, onLogout }) => {
  const [status, setStatus] = useState({ configured: false, connected: false, email: null });
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  // Active month/year being viewed. Defaults to "today" so the user
  // lands on the current month automatically. Year is a state too so
  // that creating a task for next year jumps the calendar correctly.
  const [activeMonth, setActiveMonth] = useState(() => new Date().getMonth());
  const [activeYear, setActiveYear] = useState(() => new Date().getFullYear());
  const [showCompleted, setShowCompleted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ summary: '', description: '', date: '', duration_min: 15, contact_id: '' });
  // PWA install prompt — Chrome/Android fires `beforeinstallprompt` once
  // the page is "installable" (manifest + SW + icons + https). We capture
  // the event and show our own button to trigger it.
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  // Selected event → opens detail modal with WhatsApp / phone shortcuts.
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Contacts (phonebook) — fetched once after login. The add-task modal
  // and the event-detail modal both read from this list.
  const [contacts, setContacts] = useState([]);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  // Editing an existing event opens this modal. Urgent task modal is separate.
  const [editingEvent, setEditingEvent] = useState(null);
  const [showUrgentModal, setShowUrgentModal] = useState(false);
  // Highlight a freshly-created event so the user can see where it landed.
  const [highlightId, setHighlightId] = useState(null);
  // Centered success overlay (auto-hides after 2 sec).
  const [successFlash, setSuccessFlash] = useState('');
  // Long-press drag-to-reorder state. `draggingId` = the event being
  // dragged; `dropTargetId` = the event currently under the pointer.
  // On pointer-up, if both are set and differ, we swap their start times.
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const longPressRef = useRef(null);
  const dragStartPosRef = useRef(null);
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
  const location = useLocation();

  const refresh = useCallback(async () => {
    try {
      const [s, e, c] = await Promise.all([
        axios.get(`${API}/oauth/status`, { headers: authHeaders() }),
        axios.get(`${API}/calendar/events`, { headers: authHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setEvents(e.data || []);
      setContacts(c.data || []);
    } catch (err) {
      // 401 → token invalid → kick to login
      if (err.response?.status === 401) {
        clearToken();
        window.location.reload();
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Toast on return from OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('connected') === '1') {
      toast.success('Google Calendar חובר בהצלחה');
      sync();
      // remove ?connected=1 from url
      window.history.replaceState({}, '', '/nasrallah/calendar');
    } else if (params.get('error')) {
      toast.error(`שגיאה: ${params.get('error')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      const r = await axios.get(`${API}/oauth/start`, { headers: authHeaders() });
      window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בחיבור');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('להתנתק מ-Google Calendar?')) return;
    await axios.post(`${API}/oauth/disconnect`, {}, { headers: authHeaders() });
    refresh();
    toast.success('נותק');
  };

  const sync = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/calendar/sync`, {}, { headers: authHeaders() });
      toast.success(`סונכרן · ${r.data.synced} משימות`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בסנכרון');
    }
    setBusy(false);
  };

  // Listen for the install prompt event
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const handleInstall = async () => {
    if (installEvent) {
      // Chrome / Android: trigger native install dialog
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice.outcome === 'accepted') {
        toast.success('האפליקציה הותקנה ✓');
        setInstallEvent(null);
      }
    } else {
      // iOS / browsers without programmatic install — show step-by-step help
      setShowInstallHelp(true);
    }
  };

  // Auto-poll every 5 minutes when connected
  useEffect(() => {
    if (!status.connected) return;
    const tick = setInterval(refresh, 60_000);
    return () => clearInterval(tick);
  }, [status.connected, refresh]);

  // Split events: active (incomplete) vs completed.
  const activeEvents = events.filter(e => !e.completed);
  const completedEvents = events.filter(e => e.completed);

  // Filter to active month (or completed view shows ALL completed regardless of month)
  const eventsThisMonth = (showCompleted ? completedEvents : activeEvents).filter(ev => {
    const d = (ev.start || '').slice(0, 10);
    if (!d) return false;
    const dt = new Date(d);
    return dt.getFullYear() === activeYear && dt.getMonth() === activeMonth;
  });
  const grouped = {};
  for (const ev of eventsThisMonth) {
    const d = (ev.start || '').slice(0, 10);
    grouped[d] = grouped[d] || [];
    grouped[d].push(ev);
  }
  for (const d of Object.keys(grouped)) {
    grouped[d].sort((a, b) => (a.start || '').localeCompare(b.start || ''));
  }

  // Only days that have events — hide empty days per user request 2026-04-28.
  const daysWithEvents = Object.keys(grouped).sort();
  const todayKey = new Date().toISOString().slice(0, 10);

  const goPrevMonth = () => {
    if (activeMonth === 0) { setActiveYear(y => y - 1); setActiveMonth(11); }
    else setActiveMonth(m => m - 1);
  };
  const goNextMonth = () => {
    if (activeMonth === 11) { setActiveYear(y => y + 1); setActiveMonth(0); }
    else setActiveMonth(m => m + 1);
  };

  // Scroll to the highlighted event AFTER React has rendered the new
  // month. Watching `events.length` + `activeMonth/Year` guarantees we
  // run after the DOM contains the freshly-added row, even when the
  // urgent task lands in a different month or year.
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="event-${highlightId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => clearTimeout(t);
  }, [highlightId, activeMonth, activeYear, events.length]);

  // ── Task actions ──────────────────────────────────────────────────────────
  const deleteEvent = async (ev) => {
    if (!window.confirm(`למחוק את "${ev.summary}"?`)) return;
    try {
      await axios.delete(`${API}/calendar/events/${ev.event_id}`, { headers: authHeaders() });
      setEvents(prev => prev.filter(e => e.event_id !== ev.event_id));
      toast.success('נמחק');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה במחיקה');
    }
  };

  const toggleComplete = async (ev) => {
    const next = !ev.completed;
    try {
      await axios.post(`${API}/calendar/events/${ev.event_id}/complete`,
        { completed: next }, { headers: authHeaders() });
      setEvents(prev => prev.map(e => e.event_id === ev.event_id
        ? { ...e, completed: next, completed_at: next ? new Date().toISOString() : null }
        : e));
      // Show a centered "✓ משימה בוצעה בהצלחה" overlay for 2s on completion.
      if (next) {
        setSuccessFlash('משימה בוצעה בהצלחה');
        setTimeout(() => setSuccessFlash(''), 2000);
      } else {
        toast('הוחזרה לפעיל');
      }
    } catch (err) {
      toast.error('שגיאה');
    }
  };

  const submitNewTask = async (e) => {
    e.preventDefault();
    if (!newTask.summary.trim()) { toast.error('הזן כותרת'); return; }
    try {
      const r = await axios.post(`${API}/tasks/create`, {
        summary: newTask.summary.trim(),
        description: newTask.description,
        date: newTask.date || todayKey,
        duration_min: parseInt(newTask.duration_min, 10) || 15,
        contact_id: newTask.contact_id || undefined,
      }, { headers: authHeaders() });
      const newEv = r.data;
      setEvents(prev => [...prev, newEv]);
      const pushed = newEv.pushed_days;
      if (pushed > 0) {
        toast.success(`נוסף — היום מלא, הועברה ${pushed} ימים קדימה`, { duration: 3500 });
      } else {
        toast.success('המשימה נוספה ✓');
      }
      // Jump to the month+year where the task landed and highlight it.
      // Actual scroll runs from a useEffect that fires after the DOM
      // re-renders with the new month visible.
      const landedDate = (newEv.start || '').slice(0, 10);
      if (landedDate) {
        const d = new Date(landedDate);
        setActiveYear(d.getFullYear());
        setActiveMonth(d.getMonth());
        setShowCompleted(false);
        setHighlightId(newEv.event_id);
        // Clear the highlight after 4 sec
        setTimeout(() => setHighlightId(null), 4000);
      }
      setShowAddModal(false);
      setNewTask({ summary: '', description: '', date: '', duration_min: 15, contact_id: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה ביצירה');
    }
  };

  // Urgent task: takes a precise datetime, no auto-find slot.
  const submitUrgentTask = async (data) => {
    try {
      const r = await axios.post(`${API}/tasks/urgent`, data, { headers: authHeaders() });
      const newEv = r.data;
      setEvents(prev => [...prev, newEv]);
      toast.success('משימה דחופה נוספה 🔴');
      const landedDate = (newEv.start || '').slice(0, 10);
      if (landedDate) {
        const d = new Date(landedDate);
        setActiveYear(d.getFullYear());
        setActiveMonth(d.getMonth());
        setShowCompleted(false);
        setHighlightId(newEv.event_id);
        setTimeout(() => setHighlightId(null), 4000);
      }
      setShowUrgentModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה ביצירה');
    }
  };

  const submitEditEvent = async (data) => {
    try {
      const r = await axios.put(`${API}/calendar/events/${editingEvent.event_id}`, data, { headers: authHeaders() });
      setEvents(prev => prev.map(e => e.event_id === r.data.event_id ? r.data : e));
      toast.success('המשימה עודכנה');
      setEditingEvent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בעדכון');
    }
  };

  // ── Drag-to-reorder (long-press) ──────────────────────────────────────────
  // Press and hold a task ~400ms to "pick it up", then move the finger
  // up or down. Releasing on top of another task swaps their start
  // times (and durations are preserved). Server is updated via the PUT
  // endpoint for both events.
  const swapTimes = async (a, b) => {
    const fmt = (iso) => (iso || '').slice(0, 16);
    const aStart = fmt(a.start);
    const bStart = fmt(b.start);
    if (!aStart || !bStart) return;
    const dur = (start, end) => {
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      if (!isFinite(s) || !isFinite(e) || e <= s) return 15;
      return Math.max(5, Math.round((e - s) / 60000));
    };
    const aDur = dur(a.start, a.end);
    const bDur = dur(b.start, b.end);
    // Optimistic update: swap start/end in-memory so the UI feels instant.
    setEvents(prev => prev.map(ev => {
      if (ev.event_id === a.event_id) return { ...ev, start: b.start, end: b.end };
      if (ev.event_id === b.event_id) return { ...ev, start: a.start, end: a.end };
      return ev;
    }));
    setHighlightId(a.event_id);
    setTimeout(() => setHighlightId(null), 2000);
    try {
      await Promise.all([
        axios.put(`${API}/calendar/events/${a.event_id}`,
          { datetime: bStart, duration_min: aDur }, { headers: authHeaders() }),
        axios.put(`${API}/calendar/events/${b.event_id}`,
          { datetime: aStart, duration_min: bDur }, { headers: authHeaders() }),
      ]);
      toast.success('סדר המשימות עודכן ✓');
    } catch (err) {
      toast.error('הסידור נכשל — מסנכרן');
      // Roll back via refresh on failure
      refresh();
    }
  };

  const cancelLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
  };

  const onTaskPointerDown = (ev, e) => {
    if (ev.completed) return;
    // Only left mouse button or touch
    if (e.button !== undefined && e.button !== 0) return;
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    cancelLongPress();
    longPressRef.current = setTimeout(() => {
      setDraggingId(ev.event_id);
      try { if (navigator.vibrate) navigator.vibrate(25); } catch {}
    }, 380);
  };

  const onTaskPointerMove = (e) => {
    // If the finger moves more than ~10px before the long-press timer
    // fires, treat it as a scroll and cancel the drag.
    if (!draggingId && longPressRef.current && dragStartPosRef.current) {
      const dx = Math.abs(e.clientX - dragStartPosRef.current.x);
      const dy = Math.abs(e.clientY - dragStartPosRef.current.y);
      if (dx > 10 || dy > 10) cancelLongPress();
      return;
    }
    if (!draggingId) return;
    e.preventDefault();
    const elem = document.elementFromPoint(e.clientX, e.clientY);
    if (!elem) { setDropTargetId(null); return; }
    const taskEl = elem.closest('[data-testid^="event-"]');
    if (!taskEl) { setDropTargetId(null); return; }
    const id = taskEl.getAttribute('data-testid').replace('event-', '');
    setDropTargetId(id !== draggingId ? id : null);
  };

  const onTaskPointerUp = async () => {
    cancelLongPress();
    const dragId = draggingId;
    const dropId = dropTargetId;
    setDraggingId(null);
    setDropTargetId(null);
    if (dragId && dropId && dragId !== dropId) {
      const a = events.find(x => x.event_id === dragId);
      const b = events.find(x => x.event_id === dropId);
      if (a && b) await swapTimes(a, b);
    }
  };

  // Cancel an in-progress drag on Escape (desktop).
  useEffect(() => {
    if (!draggingId) return;
    const onKey = (e) => { if (e.key === 'Escape') { setDraggingId(null); setDropTargetId(null); } };
    window.addEventListener('keyup', onKey);
    // Listen on window too so that releasing the finger outside any task
    // still ends the drag cleanly.
    const onWinUp = () => onTaskPointerUp();
    window.addEventListener('pointerup', onWinUp);
    window.addEventListener('pointercancel', onWinUp);
    return () => {
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('pointerup', onWinUp);
      window.removeEventListener('pointercancel', onWinUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingId, dropTargetId, events]);

  // ── Contact CRUD ──────────────────────────────────────────────────────────
  const saveContact = async (data) => {
    try {
      let resp;
      if (editingContact?.id) {
        resp = await axios.put(`${API}/contacts/${editingContact.id}`, data, { headers: authHeaders() });
        setContacts(prev => prev.map(c => c.id === resp.data.id ? resp.data : c));
        toast.success('איש קשר עודכן');
      } else {
        resp = await axios.post(`${API}/contacts`, data, { headers: authHeaders() });
        setContacts(prev => [...prev, resp.data].sort((a,b) => a.name.localeCompare(b.name, 'he')));
        toast.success('איש קשר נוסף');
      }
      setEditingContact(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בשמירה');
    }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`למחוק את ${c.name} ${c.last_name || ''}?`)) return;
    try {
      await axios.delete(`${API}/contacts/${c.id}`, { headers: authHeaders() });
      setContacts(prev => prev.filter(x => x.id !== c.id));
      toast.success('נמחק');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה');
    }
  };

  return (
    <Shell user={user} onLogout={onLogout} headerExtra={
      <button onClick={() => setShowContactsModal(true)} data-testid="open-contacts-btn"
        className="bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-lg p-2 transition-colors flex items-center gap-1 text-xs font-bold" title="אנשי קשר">
        <BookUser className="w-4 h-4" />
        <span className="hidden sm:inline">אנשי קשר</span>
      </button>
    }>
      <main className="p-4 max-w-3xl mx-auto">
        {/* Connection status — only the OWNER (Nasrallah) ever sees the
            "התחבר" / "נתק" / "סנכרן" controls. For Sheni and any other
            user we just show a small "מחובר" badge and let the background
            worker keep the data fresh. */}
        {!status.configured ? (
          user?.is_owner ? (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4" data-testid="oauth-not-configured">
              <p className="text-amber-900 font-bold mb-1">⚙ הגדרות Google Cloud חסרות</p>
              <p className="text-amber-800 text-xs leading-relaxed">
                עדיין לא הוזנו <code className="bg-amber-100 px-1 rounded">CLIENT_ID</code> ו-<code className="bg-amber-100 px-1 rounded">CLIENT_SECRET</code>.
                מנהל המערכת יזין אותם ב-.env ויפעיל את הסביבה מחדש.
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-4 text-amber-900 text-xs" data-testid="oauth-not-configured-viewer">
              היומן עדיין לא חובר על ידי המנהל. נא ליצור קשר עם נסראללה.
            </div>
          )
        ) : !status.connected ? (
          user?.is_owner ? (
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-4 text-center shadow-sm" data-testid="oauth-needs-connect">
              <Plug className="w-10 h-10 text-blue-700 mx-auto mb-3" />
              <p className="font-bold text-slate-900 mb-2">חיבור Google Calendar</p>
              <p className="text-slate-500 text-xs mb-4">חבר את היומן שלך כדי לסנכרן משימות אוטומטית למערכת.</p>
              <button onClick={connect} data-testid="oauth-connect-btn"
                className="bg-gradient-to-l from-blue-700 to-blue-800 hover:from-blue-600 text-white font-bold rounded-lg px-5 py-2.5 text-sm transition-all shadow-md">
                התחבר ל-Google Calendar
              </button>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-4 text-amber-900 text-xs text-center" data-testid="oauth-disconnected-viewer">
              היומן מנותק זמנית. נא ליצור קשר עם נסראללה לחידוש החיבור.
            </div>
          )
        ) : (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="oauth-connected">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-xs font-bold text-emerald-900">מחובר ליומן</p>
                {user?.is_owner && (
                  <p className="text-[10px] text-emerald-700/70" dir="ltr">{status.email}</p>
                )}
              </div>
            </div>
            {/* Only the owner sees the management buttons. Everyone else
                just sees the "מחובר" badge — no risk of accidental
                disconnect or hijacking the connection with their own Google account. */}
            {user?.is_owner && (
              <div className="flex gap-2">
                <button onClick={sync} disabled={busy} data-testid="sync-btn"
                  className="bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white text-xs font-bold rounded-lg px-3 py-1.5 flex items-center gap-1">
                  <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
                  סנכרן
                </button>
                <button onClick={disconnect} data-testid="disconnect-btn"
                  className="bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded-lg px-3 py-1.5">
                  נתק
                </button>
              </div>
            )}
          </div>
        )}

        {/* PWA install button — only show if not already installed.
            On Chrome/Android we trigger the native installer.
            On iOS we open a step-by-step modal since iOS has no API. */}
        {!isStandalone && (
          <button onClick={handleInstall} data-testid="install-app-btn"
            className="w-full bg-white border border-blue-300 hover:border-blue-500 hover:bg-blue-50 rounded-xl p-3 mb-4 flex items-center justify-between shadow-sm transition">
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-700" />
              <div className="text-right">
                <p className="text-sm font-bold text-blue-800">התקן אפליקציה לטלפון</p>
                <p className="text-[10px] text-slate-500">פתיחה ישירה ממסך הבית · ללא דפדפן</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-blue-600" />
          </button>
        )}

        {/* Top row: Add task + Urgent + Completed counter */}
        {status.connected && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <button
            onClick={() => {
              setNewTask({ summary: '', description: '', date: todayKey, duration_min: 15, contact_id: '' });
              setShowAddModal(true);
            }}
            className="bg-gradient-to-l from-blue-700 to-blue-800 hover:from-blue-600 active:scale-[0.99] text-white rounded-xl p-3 text-center shadow-md transition-all flex flex-col items-center justify-center gap-1"
            data-testid="add-task-btn"
          >
            <Plus className="w-6 h-6" strokeWidth={3} />
            <span className="text-[11px] font-black">הוסף משימה</span>
          </button>
          <button
            onClick={() => setShowUrgentModal(true)}
            className="bg-gradient-to-l from-red-600 to-red-700 hover:from-red-500 active:scale-[0.99] text-white rounded-xl p-3 text-center shadow-md transition-all flex flex-col items-center justify-center gap-1"
            data-testid="urgent-task-btn"
          >
            <Zap className="w-6 h-6" strokeWidth={2.5} fill="currentColor" />
            <span className="text-[11px] font-black">משימה דחופה</span>
          </button>
          <button
            onClick={() => setShowCompleted(v => !v)}
            className={`rounded-xl p-3 text-center shadow-sm transition-all flex flex-col items-center justify-center gap-1 ${
              showCompleted
                ? 'bg-emerald-700 text-white border-2 border-emerald-800'
                : 'bg-white text-slate-700 border border-slate-200'}`}
            data-testid="toggle-completed-btn"
          >
            <CheckSquare className="w-5 h-5" />
            <span className="text-[11px] font-black">
              {showCompleted
                ? `שלא נעשו (${activeEvents.length})`
                : `נעשו (${completedEvents.length})`}
            </span>
          </button>
        </div>
        )}

        {/* Month navigator + planner */}
        <div data-testid="events-list">
          <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3 flex items-center justify-between shadow-sm" data-testid="month-nav">
            <button onClick={goPrevMonth}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-2 flex items-center gap-1 text-sm font-bold transition" data-testid="month-prev">
              <ChevronRight className="w-4 h-4" />קודם
            </button>
            <div className="text-center">
              <p className="text-lg font-black text-slate-900" data-testid="active-month-label">
                {HEBREW_MONTHS[activeMonth]} {activeYear}
              </p>
              <p className="text-[11px] text-slate-500">
                {showCompleted ? 'מציג: שהושלמו' : `${eventsThisMonth.length} משימות פעילות החודש`}
              </p>
            </div>
            <button onClick={goNextMonth}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-2 flex items-center gap-1 text-sm font-bold transition" data-testid="month-next">
              הבא<ChevronLeft className="w-4 h-4" />
            </button>
          </div>

          {events.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm shadow-sm">
              {status.connected ? 'אין משימות. לחץ על "סנכרן" או "הוסף משימה" כדי להתחיל.' : 'התחבר ל-Google Calendar כדי לראות משימות.'}
            </div>
          ) : daysWithEvents.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm shadow-sm">
              אין משימות {showCompleted ? 'שהושלמו' : 'פעילות'} בחודש {HEBREW_MONTHS[activeMonth]}.
            </div>
          ) : (
            <div className="space-y-2">
              {daysWithEvents.map(date => {
                const dayEvents = grouped[date];
                const dt = new Date(date);
                const isToday = date === todayKey;
                const isPast = date < todayKey;
                const dayName = dt.toLocaleDateString('he-IL', { weekday: 'long' });
                const dayNum = dt.getDate();
                return (
                  <article key={date}
                    className={`bg-white border rounded-xl overflow-hidden shadow-sm transition ${
                      isToday ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}
                    data-testid={`day-${date}`}>
                    <header className={`flex items-center justify-between px-3 py-2 ${
                      isToday ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-900'}`}>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-xl font-black ${isToday ? 'text-white' : 'text-slate-900'}`}>{dayNum}</span>
                        <span className={`text-xs font-bold ${isToday ? 'text-white/90' : 'text-slate-500'}`}>{dayName}</span>
                        {isToday && <span className="text-[10px] bg-white text-blue-600 rounded-full px-2 py-0.5 font-black mr-1">היום</span>}
                      </div>
                      <span className={`text-[11px] font-bold ${isToday ? 'text-white/80' : 'text-slate-500'}`}>
                        {dayEvents.length} משימות
                      </span>
                    </header>
                    <div className="divide-y divide-slate-100">
                      {dayEvents.map(ev => {
                        const startTime = ev.all_day ? 'יום שלם'
                          : new Date(ev.start).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
                        const endTime = !ev.all_day && ev.end
                          ? new Date(ev.end).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={ev.event_id}
                            className={`px-3 py-2.5 flex items-start gap-2 cursor-pointer transition select-none ${
                              ev.urgent && !ev.completed ? 'bg-red-50 hover:bg-red-100 border-r-[6px] border-red-600' : 'hover:bg-blue-50'
                            } ${isPast && !ev.completed ? 'opacity-70' : ''} ${ev.completed ? 'opacity-60' : ''} ${
                              highlightId === ev.event_id ? 'bg-amber-100 ring-2 ring-amber-400 ring-inset animate-pulse' : ''
                            } ${draggingId === ev.event_id ? 'scale-[1.02] shadow-2xl ring-2 ring-blue-500 z-20 relative bg-white' : ''} ${
                              dropTargetId === ev.event_id ? 'bg-yellow-100 ring-2 ring-yellow-400 ring-inset' : ''
                            }`}
                            data-testid={`event-${ev.event_id}`}
                            style={{ touchAction: draggingId ? 'none' : 'auto' }}
                            onClick={() => { if (!draggingId) setSelectedEvent(ev); }}
                            onPointerDown={(e) => onTaskPointerDown(ev, e)}
                            onPointerMove={onTaskPointerMove}
                            onPointerUp={onTaskPointerUp}
                            onPointerCancel={onTaskPointerUp}
                          >
                            <button onClick={(e) => { e.stopPropagation(); toggleComplete(ev); }}
                              onPointerDown={(e) => e.stopPropagation()}
                              title="סמן כהושלמה"
                              className="flex-shrink-0 mt-1 hover:scale-110 transition" data-testid={`complete-${ev.event_id}`}>
                              {ev.completed ? (
                                <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-emerald-600 text-white shadow">
                                  <Check className="w-4 h-4" strokeWidth={3} />
                                </span>
                              ) : (
                                <span className="inline-flex w-6 h-6 items-center justify-center rounded-full border-2 border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 transition">
                                  <Check className="w-3 h-3 text-transparent hover:text-emerald-500" strokeWidth={3} />
                                </span>
                              )}
                            </button>
                            <div className={`flex-shrink-0 text-center min-w-[60px] py-1 px-2 rounded-lg border ${
                              ev.urgent && !ev.completed ? 'bg-red-100 border-red-300' : 'bg-blue-50 border-blue-200'
                            }`}>
                              <p className={`text-sm font-black leading-none ${ev.urgent && !ev.completed ? 'text-red-700' : 'text-blue-800'}`} dir="ltr">{startTime}</p>
                              {endTime && <p className={`text-[10px] mt-0.5 ${ev.urgent && !ev.completed ? 'text-red-600' : 'text-blue-600'}`} dir="ltr">עד {endTime}</p>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {ev.urgent && !ev.completed && (
                                  <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-black rounded-full px-2 py-0.5 shadow-sm">
                                    <AlertTriangle className="w-3 h-3" strokeWidth={3} />
                                    דחוף
                                  </span>
                                )}
                                <p className={`font-bold text-sm truncate ${ev.urgent && !ev.completed ? 'text-red-900' : 'text-slate-900'} ${ev.completed ? 'line-through' : ''}`}>
                                  {ev.summary || '(ללא כותרת)'}
                                </p>
                              </div>
                              {ev.description && (
                                <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2 whitespace-pre-line">{ev.description}</p>
                              )}
                              {(ev.location || ev.attendees?.length > 0) && (
                                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                                  {ev.location && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /><span className="truncate max-w-[140px]">{ev.location}</span></span>}
                                  {ev.attendees?.length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.attendees.length}</span>}
                                </div>
                              )}
                            </div>
                            {/* Drag handle hint — long-press anywhere on the row works,
                                but the icon makes the affordance discoverable. */}
                            <span className="flex-shrink-0 mt-1 text-slate-300" title="לחץ ארוכות לסידור מחדש">
                              <GripVertical className="w-4 h-4" />
                            </span>
                            <button onClick={(e) => { e.stopPropagation(); setEditingEvent(ev); }}
                              onPointerDown={(e) => e.stopPropagation()}
                              title="ערוך"
                              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-blue-100 text-blue-700 transition" data-testid={`edit-${ev.event_id}`}>
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); deleteEvent(ev); }}
                              onPointerDown={(e) => e.stopPropagation()}
                              title="מחק"
                              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-red-100 text-red-600 transition" data-testid={`delete-${ev.event_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        {/* Event detail modal — opens when a row is clicked.
            Shows full info + WhatsApp / phone shortcuts. The phone field
            will later be auto-populated via a phonebook sync; for now it
            looks for any digits in description/location/summary, falling
            back to the in-app placeholder action. */}
        {selectedEvent && (() => {
          const ev = selectedEvent;
          const close = () => setSelectedEvent(null);
          const startDt = ev.start ? new Date(ev.start) : null;
          const endDt = ev.end ? new Date(ev.end) : null;
          const dateLabel = startDt
            ? startDt.toLocaleDateString('he-IL', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
            : '';
          const timeLabel = ev.all_day ? 'יום שלם'
            : startDt && endDt
              ? `${startDt.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} – ${endDt.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`
              : '';
          // Best phone source: linked contact, then regex from event text.
          let phone = ev.contact_phone || null;
          if (!phone) {
            const haystack = `${ev.summary || ''} ${ev.description || ''} ${ev.location || ''}`;
            const phoneMatch = haystack.match(/(?:\+?972[\-\s]?|0)5\d[\-\s]?\d{3}[\-\s]?\d{4}/);
            phone = phoneMatch ? phoneMatch[0].replace(/[\-\s]/g, '') : null;
          }
          // Strip any "+" then format for tel: and wa.me
          const phoneClean = phone ? phone.replace(/\+/g, '') : null;
          const telPhone = phoneClean
            ? (phoneClean.startsWith('972') ? '+' + phoneClean : phoneClean)
            : null;
          const waPhone = phoneClean
            ? (phoneClean.startsWith('972') ? phoneClean : phoneClean.replace(/^0/, '972'))
            : null;
          return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={close} data-testid="event-detail-modal">
              <div onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                {/* Header */}
                <header className="bg-gradient-to-l from-blue-700 to-blue-800 text-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-blue-100 mb-1">{dateLabel}</p>
                      <h3 className={`font-black text-lg leading-tight ${ev.completed ? 'line-through opacity-80' : ''}`} data-testid="modal-summary">
                        {ev.summary || '(ללא כותרת)'}
                      </h3>
                    </div>
                    <button onClick={close} className="flex-shrink-0 text-white/70 hover:text-white" data-testid="modal-close">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  {timeLabel && (
                    <div className="flex items-center gap-2 mt-3 bg-white/15 rounded-lg px-3 py-1.5 w-fit">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-bold" dir="ltr">{timeLabel}</span>
                    </div>
                  )}
                </header>

                {/* Quick actions: WhatsApp + Phone */}
                <div className="p-4 grid grid-cols-2 gap-2 border-b border-slate-100">
                  <a
                    href={waPhone ? `https://wa.me/${waPhone}` : '#'}
                    onClick={(e) => { if (!waPhone) { e.preventDefault(); toast('מספר טלפון יסונכרן בהמשך', { icon: '📞' }); } }}
                    target={waPhone ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className={`flex items-center justify-center gap-2 rounded-xl py-3 font-black text-sm shadow-md transition ${
                      waPhone
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-emerald-100 text-emerald-700 border-2 border-dashed border-emerald-300'}`}
                    data-testid="modal-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    וואטסאפ
                  </a>
                  <a
                    href={telPhone ? `tel:${telPhone}` : '#'}
                    onClick={(e) => { if (!telPhone) { e.preventDefault(); toast('מספר טלפון יסונכרן בהמשך', { icon: '📞' }); } }}
                    className={`flex items-center justify-center gap-2 rounded-xl py-3 font-black text-sm shadow-md transition ${
                      telPhone
                        ? 'bg-blue-700 hover:bg-blue-800 text-white'
                        : 'bg-blue-100 text-blue-700 border-2 border-dashed border-blue-300'}`}
                    data-testid="modal-phone-btn"
                  >
                    <Phone className="w-5 h-5" />
                    התקשר
                  </a>
                </div>

                {/* Full details */}
                <div className="p-4 space-y-3 text-sm max-h-[50vh] overflow-y-auto">
                  {ev.description && (
                    <div className="flex items-start gap-2">
                      <AlignLeft className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">תיאור</p>
                        <p className="text-slate-700 whitespace-pre-line break-words" data-testid="modal-description">{ev.description}</p>
                      </div>
                    </div>
                  )}
                  {ev.location && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">מיקום</p>
                        <p className="text-slate-700 break-words">{ev.location}</p>
                      </div>
                    </div>
                  )}
                  {ev.attendees?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">משתתפים ({ev.attendees.length})</p>
                        <div className="space-y-0.5">
                          {ev.attendees.map(a => <p key={a} className="text-slate-700 text-xs" dir="ltr">{a}</p>)}
                        </div>
                      </div>
                    </div>
                  )}
                  {phone && (
                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                          {ev.contact_name ? `איש קשר · ${ev.contact_name}` : 'טלפון שזוהה'}
                        </p>
                        <p className="text-slate-700 font-mono" dir="ltr">{telPhone || phone}</p>
                      </div>
                    </div>
                  )}
                  {ev.completed && ev.completed_at && (
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-1">הושלמה</p>
                        <p className="text-emerald-700 text-xs" dir="ltr">{new Date(ev.completed_at).toLocaleString('he-IL')}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <footer className="p-3 bg-slate-50 border-t border-slate-100 flex gap-2">
                  <button
                    onClick={() => { toggleComplete(ev); setSelectedEvent({ ...ev, completed: !ev.completed }); }}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 font-bold text-sm transition ${
                      ev.completed
                        ? 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                    data-testid="modal-toggle-complete"
                  >
                    <CheckSquare className="w-4 h-4" />
                    {ev.completed ? 'החזר לפעילה' : 'סמן הושלמה'}
                  </button>
                  {ev.html_link && (
                    <a href={ev.html_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1 rounded-lg py-2.5 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-xs transition" title="פתח ב-Google">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => { deleteEvent(ev); close(); }}
                    className="flex items-center justify-center gap-1 rounded-lg py-2.5 px-3 bg-red-100 hover:bg-red-200 text-red-700 font-bold text-xs transition"
                    title="מחק"
                    data-testid="modal-delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </footer>
              </div>
            </div>
          );
        })()}

        {/* Centered success overlay (auto-hides after 2s) */}
        {successFlash && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none" data-testid="success-flash">
            <div className="bg-emerald-600 text-white rounded-2xl px-6 py-5 shadow-2xl flex items-center gap-3 animate-[fadeIn_.2s_ease-out]">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-white text-emerald-600">
                <Check className="w-6 h-6" strokeWidth={4} />
              </span>
              <span className="font-black text-base">{successFlash}</span>
            </div>
          </div>
        )}

        {/* Urgent task modal — exact datetime, lets you slot something
            outside or inside the schedule with the 🔴 prefix. */}
        {showUrgentModal && (
          <UrgentTaskModal
            contacts={contacts}
            onSave={submitUrgentTask}
            onCancel={() => setShowUrgentModal(false)}
          />
        )}

        {/* Edit existing event modal */}
        {editingEvent && (
          <EditEventModal
            event={editingEvent}
            contacts={contacts}
            onSave={submitEditEvent}
            onCancel={() => setEditingEvent(null)}
          />
        )}

        {/* Contacts modal — full CRUD over the local phonebook. */}
        {showContactsModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => { setShowContactsModal(false); setEditingContact(null); }} data-testid="contacts-modal">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
              <header className="bg-gradient-to-l from-blue-700 to-blue-800 text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookUser className="w-5 h-5" />
                  <h3 className="font-black text-lg">אנשי קשר ({contacts.length})</h3>
                </div>
                <button onClick={() => { setShowContactsModal(false); setEditingContact(null); }} className="text-white/70 hover:text-white" data-testid="contacts-close">
                  <X className="w-5 h-5" />
                </button>
              </header>

              {editingContact !== null ? (
                <ContactForm
                  initial={editingContact}
                  onSave={saveContact}
                  onCancel={() => setEditingContact(null)}
                />
              ) : (
                <>
                  <div className="p-3 border-b border-slate-100">
                    <button onClick={() => setEditingContact({})} data-testid="add-contact-btn"
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg py-2.5 flex items-center justify-center gap-2 transition shadow-sm">
                      <UserPlus className="w-4 h-4" /> הוסף איש קשר
                    </button>
                    <div className="relative mt-2">
                      <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                      <input type="text" value={contactSearch}
                        onChange={e => setContactSearch(e.target.value)}
                        placeholder="חיפוש לפי שם / טלפון..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg pr-9 pl-3 py-2 text-sm outline-none focus:border-blue-600"
                        data-testid="contacts-search" />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {contacts.length === 0 ? (
                      <div className="p-8 text-center text-slate-500 text-sm">
                        אין אנשי קשר עדיין. לחץ על "הוסף איש קשר".
                      </div>
                    ) : (
                      contacts
                        .filter(c => {
                          if (!contactSearch) return true;
                          const q = contactSearch.toLowerCase();
                          return (c.name || '').toLowerCase().includes(q)
                            || (c.last_name || '').toLowerCase().includes(q)
                            || (c.phone || '').includes(contactSearch);
                        })
                        .map(c => (
                          <article key={c.id} className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 hover:bg-blue-50 transition" data-testid={`contact-${c.id}`}>
                            <div className="flex-shrink-0 w-9 h-9 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-black text-sm">
                              {(c.name || '?').charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm text-slate-900 truncate">{c.name} {c.last_name || ''}</p>
                              <p className="text-[11px] text-slate-500" dir="ltr">{c.phone}</p>
                              {c.email && <p className="text-[10px] text-slate-400 truncate" dir="ltr">{c.email}</p>}
                            </div>
                            <a href={`tel:${c.phone}`} className="p-1.5 text-blue-700 hover:bg-blue-100 rounded-lg transition" title="התקשר">
                              <Phone className="w-4 h-4" />
                            </a>
                            <a href={`https://wa.me/${(c.phone || '').replace(/^\+/, '')}`}
                              target="_blank" rel="noopener noreferrer"
                              className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded-lg transition" title="וואטסאפ">
                              <MessageCircle className="w-4 h-4" />
                            </a>
                            <button onClick={() => setEditingContact(c)}
                              className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition" title="ערוך">
                              <AlignLeft className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteContact(c)}
                              className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition" title="מחק" data-testid={`delete-contact-${c.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </article>
                        ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Install help modal (iOS / browsers without prompt API) */}
        {showInstallHelp && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowInstallHelp(false)} data-testid="install-help-modal">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-black text-lg text-slate-900 flex items-center gap-2">
                  <Download className="w-5 h-5 text-blue-700" />
                  התקנת האפליקציה
                </h3>
                <button onClick={() => setShowInstallHelp(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {(() => {
                const dev = detectDevice();
                if (dev === 'ios') {
                  return (
                    <div className="space-y-3 text-sm text-slate-700">
                      <p className="font-bold text-blue-800">📱 לאייפון (Safari):</p>
                      <ol className="space-y-2 mr-5 list-decimal text-sm">
                        <li>פתח את הדף ב-<b>Safari</b> (לא ב-Chrome).</li>
                        <li>לחץ על כפתור <b>השיתוף</b> בתחתית המסך (ריבוע עם חץ למעלה).</li>
                        <li>גלול ובחר <b>"הוסף למסך הבית"</b> / <i>Add to Home Screen</i>.</li>
                        <li>לחץ <b>"הוסף"</b> בפינה הימנית העליונה.</li>
                      </ol>
                      <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 mt-3">
                        ⚠ אם אתה ב-Chrome ב-iOS — חזור לפתוח את הקישור ב-Safari.
                      </p>
                    </div>
                  );
                }
                if (dev === 'android') {
                  return (
                    <div className="space-y-3 text-sm text-slate-700">
                      <p className="font-bold text-blue-800">📱 לאנדרואיד (Chrome):</p>
                      <ol className="space-y-2 mr-5 list-decimal text-sm">
                        <li>לחץ על כפתור <b>3 הנקודות</b> (⋮) בפינה הימנית העליונה.</li>
                        <li>בחר <b>"הוסף למסך הבית"</b> או <i>"Install app"</i>.</li>
                        <li>אשר את ההתקנה.</li>
                      </ol>
                      <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 mt-3">
                        💡 אם רואה כפתור "התקן" אוטומטי — לחץ עליו והכל יקרה אוטומטית.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-3 text-sm text-slate-700">
                    <p className="font-bold text-blue-800">💻 בדפדפן Chrome/Edge:</p>
                    <ol className="space-y-2 mr-5 list-decimal text-sm">
                      <li>חפש סמל <b>התקנה</b> (כמו ⊕) בפינה הימנית של שורת הכתובת.</li>
                      <li>לחץ עליו → <b>"התקן"</b>.</li>
                    </ol>
                    <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 mt-3">
                      💡 הכי מומלץ — פתח את הקישור בטלפון נייד והתקן משם.
                    </p>
                  </div>
                );
              })()}
              <button onClick={() => setShowInstallHelp(false)} data-testid="install-help-close"
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg py-2.5 mt-4 transition">
                הבנתי
              </button>
            </div>
          </div>
        )}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowAddModal(false)} data-testid="add-task-modal">
            <form onSubmit={submitNewTask} onClick={e => e.stopPropagation()}
              className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-4 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-black text-lg text-slate-900">הוספת משימה חדשה</h3>
                <button type="button" onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 mb-1 block">איש קשר (אופציונלי)</label>
                  <select value={newTask.contact_id}
                    onChange={e => setNewTask({ ...newTask, contact_id: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
                    data-testid="new-task-contact">
                    <option value="">— ללא —</option>
                    {contacts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.last_name || ''} {c.phone ? `· ${c.phone}` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={() => { setEditingContact({}); setShowContactsModal(true); }}
                    className="text-[11px] text-blue-700 hover:underline mt-1 flex items-center gap-1" data-testid="quick-add-contact">
                    <UserPlus className="w-3 h-3" /> הוסף איש קשר חדש
                  </button>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 mb-1 block">כותרת המשימה *</label>
                  <input type="text" required value={newTask.summary}
                    onChange={e => setNewTask({ ...newTask, summary: e.target.value })}
                    placeholder="למשל: פגישה עם רואה חשבון"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                    data-testid="new-task-summary" autoFocus />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 mb-1 block">תאריך מועדף</label>
                  <input type="date" value={newTask.date || todayKey}
                    onChange={e => setNewTask({ ...newTask, date: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
                    data-testid="new-task-date" />
                  <p className="text-[10px] text-slate-500 mt-1">
                    שעה אוטומטית בין 10:00 ל-18:00. אם היום מלא — תועבר ליום הבא.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 mb-1 block">משך (דקות)</label>
                  <select value={newTask.duration_min}
                    onChange={e => setNewTask({ ...newTask, duration_min: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
                    data-testid="new-task-duration">
                    <option value="15">15 דקות</option>
                    <option value="30">30 דקות</option>
                    <option value="45">45 דקות</option>
                    <option value="60">שעה</option>
                    <option value="90">שעה וחצי</option>
                    <option value="120">שעתיים</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 mb-1 block">תיאור (אופציונלי)</label>
                  <textarea value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    rows={2} placeholder="פרטים נוספים..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 resize-none"
                    data-testid="new-task-description" />
                </div>
                <button type="submit" data-testid="new-task-submit"
                  className="w-full bg-gradient-to-l from-blue-700 to-blue-800 hover:from-blue-600 text-white font-black rounded-lg py-3 transition shadow-md">
                  הוסף ליומן
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="mt-6 pb-6 text-center">
          <p className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            סביבת עבודה פרטית · נתונים מוצפנים
          </p>
        </div>
      </main>
    </Shell>
  );
};

// ─── Urgent task modal — exact datetime input, no auto-find slot ─────────
const UrgentTaskModal = ({ contacts, onSave, onCancel }) => {
  const now = new Date();
  // Default to current time + 30 minutes, formatted YYYY-MM-DDTHH:MM
  const defaultDt = new Date(now.getTime() + 30 * 60000);
  const pad = (n) => String(n).padStart(2, '0');
  const defaultIso = `${defaultDt.getFullYear()}-${pad(defaultDt.getMonth()+1)}-${pad(defaultDt.getDate())}T${pad(defaultDt.getHours())}:${pad(defaultDt.getMinutes())}`;
  const [form, setForm] = useState({
    summary: '', description: '', datetime: defaultIso, duration_min: 30, contact_id: '',
  });
  const submit = (e) => {
    e.preventDefault();
    if (!form.summary.trim()) { toast.error('הזן כותרת'); return; }
    onSave({
      summary: form.summary.trim(),
      description: form.description,
      datetime: form.datetime,
      duration_min: parseInt(form.duration_min, 10) || 30,
      contact_id: form.contact_id || undefined,
    });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={onCancel} data-testid="urgent-task-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-4 shadow-2xl border-t-4 border-red-500">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-lg text-red-700 flex items-center gap-2">
            <Zap className="w-5 h-5" fill="currentColor" /> משימה דחופה
          </h3>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">כותרת *</label>
            <input type="text" required value={form.summary}
              onChange={e => setForm({ ...form, summary: e.target.value })}
              placeholder="פרטי המשימה הדחופה"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
              data-testid="urgent-summary" autoFocus />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">תאריך ושעה מדויקים *</label>
            <input type="datetime-local" required value={form.datetime}
              onChange={e => setForm({ ...form, datetime: e.target.value })}
              dir="ltr"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500"
              data-testid="urgent-datetime" />
            <p className="text-[10px] text-red-700 mt-1 font-bold">תיכנס בדיוק במועד שתבחר — גם מחוץ לשעות הרגילות.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">משך</label>
              <select value={form.duration_min}
                onChange={e => setForm({ ...form, duration_min: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="15">15 דקות</option>
                <option value="30">30 דקות</option>
                <option value="60">שעה</option>
                <option value="120">שעתיים</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">איש קשר</label>
              <select value={form.contact_id}
                onChange={e => setForm({ ...form, contact_id: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">— ללא —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name} {c.last_name || ''}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">תיאור (אופציונלי)</label>
            <textarea value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500 resize-none" />
          </div>
          <button type="submit" data-testid="urgent-submit"
            className="w-full bg-gradient-to-l from-red-600 to-red-700 hover:from-red-500 text-white font-black rounded-lg py-3 transition shadow-md flex items-center justify-center gap-2">
            <Zap className="w-4 h-4" fill="currentColor" />
            הוסף משימה דחופה
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Edit existing event modal — title / description / time ──────────────
const EditEventModal = ({ event, contacts, onSave, onCancel }) => {
  const pad = (n) => String(n).padStart(2, '0');
  const start = event.start ? new Date(event.start) : new Date();
  const end = event.end ? new Date(event.end) : new Date(start.getTime() + 15 * 60000);
  const initialDt = `${start.getFullYear()}-${pad(start.getMonth()+1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}`;
  const initialDuration = Math.max(5, Math.round((end - start) / 60000));
  const [form, setForm] = useState({
    summary: event.summary || '',
    description: event.description || '',
    datetime: initialDt,
    duration_min: initialDuration,
    contact_id: event.contact_id || '',
  });
  const submit = (e) => {
    e.preventDefault();
    if (!form.summary.trim()) { toast.error('הזן כותרת'); return; }
    onSave({
      summary: form.summary.trim(),
      description: form.description,
      datetime: form.datetime,
      duration_min: parseInt(form.duration_min, 10) || 15,
      contact_id: form.contact_id,
    });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={onCancel} data-testid="edit-event-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-4 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-lg text-slate-900 flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-700" /> עריכת משימה
          </h3>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">כותרת *</label>
            <input type="text" required value={form.summary}
              onChange={e => setForm({ ...form, summary: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
              data-testid="edit-summary" autoFocus />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">תאריך ושעה</label>
            <input type="datetime-local" value={form.datetime}
              onChange={e => setForm({ ...form, datetime: e.target.value })}
              dir="ltr"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
              data-testid="edit-datetime" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">משך (דק׳)</label>
              <input type="number" min="5" max="240" value={form.duration_min}
                onChange={e => setForm({ ...form, duration_min: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">איש קשר</label>
              <select value={form.contact_id}
                onChange={e => setForm({ ...form, contact_id: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">— ללא —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name} {c.last_name || ''}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">תיאור</label>
            <textarea value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 resize-none" />
          </div>
          <button type="submit" data-testid="edit-submit"
            className="w-full bg-gradient-to-l from-blue-700 to-blue-800 hover:from-blue-600 text-white font-black rounded-lg py-3 transition shadow-md">
            שמור שינויים
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Contact form (add / edit) ─────────────────────────────────────────────
const ContactForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: initial?.name || '',
    last_name: initial?.last_name || '',
    id_number: initial?.id_number || '',
    email: initial?.email || '',
    // Phone in display form (without +972) for easier typing.
    // Backend normalises whatever we send, so we just send the raw text.
    phone: initial?.phone || '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('חסר שם'); return; }
    if (!form.phone.trim()) { toast.error('חסר טלפון'); return; }
    setBusy(true);
    await onSave(form);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="p-4 space-y-3 overflow-y-auto" data-testid="contact-form">
      <p className="font-bold text-sm text-slate-700">
        {initial?.id ? 'עריכת איש קשר' : 'הוספת איש קשר חדש'}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-bold text-slate-700 mb-1 block">שם פרטי *</label>
          <input type="text" required value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
            data-testid="contact-form-name" autoFocus />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 mb-1 block">שם משפחה</label>
          <input type="text" value={form.last_name}
            onChange={e => setForm({ ...form, last_name: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600"
            data-testid="contact-form-lastname" />
        </div>
      </div>
      <div>
        <label className="text-xs font-bold text-slate-700 mb-1 block">
          טלפון נייד * <span className="text-blue-600 text-[10px]">(אוטומטית +972)</span>
        </label>
        <input type="tel" required value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          placeholder="052-1234567 או +972521234567"
          dir="ltr"
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 text-left"
          data-testid="contact-form-phone" />
        <p className="text-[10px] text-slate-500 mt-1">המספר ינורמל אוטומטית לפורמט בינלאומי לזיהוי וואטסאפ</p>
      </div>
      <div>
        <label className="text-xs font-bold text-slate-700 mb-1 block">ת.ז (אופציונלי)</label>
        <input type="text" value={form.id_number}
          onChange={e => setForm({ ...form, id_number: e.target.value })}
          placeholder="9 ספרות"
          dir="ltr"
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 text-left"
          data-testid="contact-form-id" />
      </div>
      <div>
        <label className="text-xs font-bold text-slate-700 mb-1 block">אימייל (אופציונלי)</label>
        <input type="email" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          dir="ltr"
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 text-left"
          data-testid="contact-form-email" />
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" onClick={onCancel}
          className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg py-2.5 transition" data-testid="contact-form-cancel">
          ביטול
        </button>
        <button type="submit" disabled={busy}
          className="flex-1 bg-gradient-to-l from-blue-700 to-blue-800 hover:from-blue-600 disabled:opacity-50 text-white font-black rounded-lg py-2.5 transition shadow-md" data-testid="contact-form-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (initial?.id ? 'עדכן' : 'שמור')}
        </button>
      </div>
    </form>
  );
};

// ─── Main router ────────────────────────────────────────────────────────────
const NasrallahApp = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(r => setUser(r.data))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  // Skip auth gate for the OAuth callback path (server-side RedirectResponse).
  const isOAuthReturn = location.search.includes('connected=1') || location.search.includes('error=');

  const handleLoggedIn = (data) => {
    setUser({ name: data.name, username: data.username });
    navigate('/nasrallah/calendar', { replace: true });
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() }); } catch {}
    clearToken();
    setUser(null);
    navigate('/nasrallah', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-700 animate-spin" />
      </div>
    );
  }

  if (!user && !isOAuthReturn) {
    return <LoginScreen onLoggedIn={handleLoggedIn} />;
  }

  return (
    <Routes>
      <Route path="/" element={<CalendarPage user={user} onLogout={handleLogout} />} />
      <Route path="/calendar" element={<CalendarPage user={user} onLogout={handleLogout} />} />
      <Route path="/app" element={<CalendarPage user={user} onLogout={handleLogout} />} />
    </Routes>
  );
};

export default NasrallahApp;
